import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { create } from "zustand";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { audioPlayer } from "~/services/audioPlayer.service";
import { castService } from "~/services/cast.service";
import type { Song } from "~/types";
import type { SongAction, SongActionsContext } from "./songActions.types";
import { getActionsFactory, type ActionsVariant } from "./songActionsFactory";


// ---------------------------------------------------------------------------
// State & Actions
// ---------------------------------------------------------------------------

export interface AudioStoreState {
  currentSong: Song | null;
  playlist: Song[];
  currentIndex: number;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  loop: boolean;
  shuffle: boolean;
  originalPlaylist: Song[];
  bigPlayerOpen: boolean;
  /** Router instance – set once by AudioElements on mount */
  router: AppRouterInstance | null;
  /** Resolved actions for the current song */
  currentSongActions: SongAction[];
  /** Actions variant stored from last playSong call */
  getActionsBuilder: ActionsVariant | null;
  isCasting: boolean;
  castingDeviceName: string | null;

  setIsCasting(isCasting: boolean, deviceName: string | null): void;

  playSong(song: Song, playlist: Song[], actionsVariant?: ActionsVariant): void;
  pause(): void;
  resume(): void;
  next(): Song | null;
  prev(): Song | null;
  seek(time: number): void;
  skip(seconds: number): void;
  toggleLoop(): void;
  toggleShuffle(): void;
  open(navigationType?: "push" | "replace" | "auto"): void;
  close(): void;
  setBigPlayerOpen(open: boolean): void;
  setRouter(router: AppRouterInstance): void;
  /** Merge partial updates into the current song (e.g. after rename) */
  updateCurrentSong(updates: Partial<Song>): void;
}

const getRouteState = (song: Song) => {
  let safeToGoBack = false;
  let prevPageUrl = window.location.pathname + window.location.search;
  if (prevPageUrl === song.frontendUrl) {
    // If the current URL is already the song URL, try to use the previous URL from history state (if it exists)
    const crtState = window.history.state;
    if (
      crtState &&
      crtState.prevPageUrl &&
      crtState.prevPageUrl !== song.frontendUrl
    ) {
      prevPageUrl = crtState.prevPageUrl;
    } else {
      // If we can't find a reasonable previous URL, default to homepage
      prevPageUrl = "/";
    }
  } else {
    // If the current URL is different from the song URL, it's safe to go back to it when closing the mini player
    safeToGoBack = true;
  }

  return { prevPageUrl, safeToGoBack, miniPlayerDialog: true, songId: song.id };
};

const buildVisibleActions = (
  variant: ActionsVariant | null,
  song: Song,
  context: SongActionsContext,
): SongAction[] => {
  const builder = variant ? getActionsFactory(variant) : null;
  return builder ? builder(song, context).filter((a) => a.visible) : [];
};

export const audioStore = create<AudioStoreState>((set, get) => {
  // Subscribe to audioPlayer events (singleton — set up once at module init)
  audioPlayer.on("play", () => set({ isPlaying: true }));
  audioPlayer.on("pause", () => set({ isPlaying: false }));
  audioPlayer.on("timeupdate", (t: unknown) =>
    set({ currentTime: t as number }),
  );
  audioPlayer.on("duration", (d: unknown) => set({ duration: d as number }));
  audioPlayer.on("ended", () => {
    const s = get();
    set({ isPlaying: false });

    // If casting is active, the remote device handles ended events
    if (s.isCasting) return;

    // If there's no current song or no playlist, nothing to do
    if (!s.currentSong || !s.playlist || s.playlist.length === 0) return;

    // If playlist only contains the current song
    if (s.playlist.length <= 1) {
      if (s.loop) {
        audioPlayer.seek(0);
        audioPlayer.play();
      }
      return;
    }

    // Robustly compute the current index from currentSong id (handles stale currentIndex)
    const curIdx = s.playlist.findIndex((p) => p.id === s.currentSong?.id);
    const baseIdx = curIdx >= 0 ? curIdx : s.currentIndex;

    const nextIdx = s.loop
      ? (baseIdx + 1) % s.playlist.length
      : baseIdx + 1 < s.playlist.length
        ? baseIdx + 1
        : -1;

    if (nextIdx < 0) return;

    const nextSong = s.playlist[nextIdx];
    const actions = buildVisibleActions(s.getActionsBuilder, nextSong, {
      router: s.router!,
      getState: get,
    });
    set({
      currentIndex: nextIdx,
      currentSong: nextSong,
      currentTime: 0,
      duration: 0,
      currentSongActions: actions,
    });
    audioPlayer.setSource(nextSong.audioSrc);
    audioPlayer.play();

    //update the URL if the mini player is open
    if (s.bigPlayerOpen) {
      window.history.replaceState(
        getRouteState(nextSong),
        "",
        nextSong.frontendUrl,
      );
    }
  });

  // Close dialog when user navigates back (popstate)
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", (e: PopStateEvent) => {
      const st = e.state ?? window.history.state;
      if (!st || !st.miniPlayerDialog) {
        // ensure store state is cleared when user navigates back
        set({ bigPlayerOpen: false });
      }
    });
  }

  return {
    currentSong: null,
    playlist: [],
    currentIndex: -1,
    isPlaying: false,
    isCasting: false,
    castingDeviceName: null,
    currentTime: 0,
    duration: 0,
    loop: false,
    shuffle: false,
    originalPlaylist: [],
    bigPlayerOpen: false,
    currentSongActions: [],
    getActionsBuilder: null,
    router: null,

    setIsCasting(isCasting: boolean, deviceName: string | null) {
      set({ isCasting, castingDeviceName: deviceName });
    },

    playSong(song: Song, playlist: Song[], actionsVariant?: ActionsVariant) {
      const s = get();
      const idx = playlist.findIndex((s2) => s2.id === song.id);
      const variant = actionsVariant ?? s.getActionsBuilder;
      const actions = buildVisibleActions(variant ?? null, song, {
        router: s.router!,
        getState: get,
      });
      set({
        currentSong: song,
        playlist,
        originalPlaylist: playlist,
        currentIndex: idx >= 0 ? idx : 0,
        getActionsBuilder: variant ?? null,
        currentSongActions: actions,
      });

      // Trigger lazy Cast SDK loading on the first playSong call.
      castService.loadSdk();

      if (s.isCasting) {
        castService.loadMedia(song, 0, true);
      } else {
        audioPlayer.setSource(song.audioSrc);
        audioPlayer.play();
      }

      trackEvent(AnalyticsEvents.SONG_PLAYER_PLAY, {
        songId: song.id,
        title: song.title,
      });
    },

    pause() {
      const s = get();
      if (s.isCasting) {
        const cc = castService.getController();
        if (cc && !cc.player.isPaused && cc.player.canPause)
          cc.controller.playOrPause();
        const song = s.currentSong;
        if (song)
          trackEvent(AnalyticsEvents.SONG_PLAYER_PAUSE, {
            songId: song.id,
            title: song.title,
          });
        return;
      }

      audioPlayer.pause();
      const song = s.currentSong;
      if (song) {
        trackEvent(AnalyticsEvents.SONG_PLAYER_PAUSE, {
          songId: song.id,
          title: song.title,
        });
      }
    },

    resume() {
      const s = get();
      if (s.isCasting) {
        const cc = castService.getController();
        if (cc && cc.player.isPaused) cc.controller.playOrPause();
        const song = s.currentSong;
        if (song)
          trackEvent(AnalyticsEvents.SONG_PLAYER_PLAY, {
            songId: song.id,
            title: song.title,
          });
        return;
      }

      audioPlayer.play();
      const song = s.currentSong;
      if (song) {
        trackEvent(AnalyticsEvents.SONG_PLAYER_PLAY, {
          songId: song.id,
          title: song.title,
        });
      }
    },

    next() {
      const s = get();
      if (s.playlist.length === 0) return null;
      const baseIdx = s.currentIndex;
      const nextIdx = s.loop
        ? (baseIdx + 1) % s.playlist.length
        : baseIdx + 1 < s.playlist.length
          ? baseIdx + 1
          : -1;
      if (nextIdx < 0) return null;
      const nextSong = s.playlist[nextIdx];
      const actions = buildVisibleActions(s.getActionsBuilder, nextSong, {
        router: s.router!,
        getState: get,
      });
      set({
        currentIndex: nextIdx,
        currentSong: nextSong,
        currentTime: 0,
        duration: 0,
        currentSongActions: actions,
      });
      if (s.isCasting) {
        castService.loadMedia(nextSong, 0, true);
      } else {
        audioPlayer.setSource(nextSong.audioSrc);
        audioPlayer.play();
      }
      trackEvent(AnalyticsEvents.SONG_PLAYER_NEXT, {
        songId: nextSong.id,
        title: nextSong.title,
      });
      return nextSong;
    },

    prev() {
      const s = get();
      if (s.playlist.length === 0) return null;
      const baseIdx = s.currentIndex;
      const prevIdx = s.loop
        ? (baseIdx - 1 + s.playlist.length) % s.playlist.length
        : baseIdx - 1 >= 0
          ? baseIdx - 1
          : -1;
      if (prevIdx < 0) return null;
      const prevSong = s.playlist[prevIdx];
      const actions = buildVisibleActions(s.getActionsBuilder, prevSong, {
        router: s.router!,
        getState: get,
      });
      set({
        currentIndex: prevIdx,
        currentSong: prevSong,
        currentTime: 0,
        duration: 0,
        currentSongActions: actions,
      });
      if (s.isCasting) {
        castService.loadMedia(prevSong, 0, true);
      } else {
        audioPlayer.setSource(prevSong.audioSrc);
        audioPlayer.play();
      }
      trackEvent(AnalyticsEvents.SONG_PLAYER_PREV, {
        songId: prevSong.id,
        title: prevSong.title,
      });
      return prevSong;
    },

    seek(time: number) {
      const s = get();
      if (s.isCasting) {
        const cc = castService.getController();
        if (cc && cc.player.canSeek) {
          cc.player.currentTime = time;
          cc.controller.seek();
        }
        return;
      }
      audioPlayer.seek(time);
    },

    toggleLoop() {
      const s = get();
      const newLoop = !s.loop;
      set({ loop: newLoop });
      const song = s.currentSong;
      if (song) {
        trackEvent(AnalyticsEvents.SONG_PLAYER_LOOP_TOGGLE, {
          songId: song.id,
          title: song.title,
          loop: newLoop,
        });
      }
    },

    toggleShuffle() {
      const s = get();
      // Turning shuffle on
      if (!s.shuffle) {
        const original = s.playlist.slice();
        const current = s.currentSong;
        // Fisher-Yates shuffle for rest of songs (keep current first)
        const rest = original.filter((p) => !current || p.id !== current.id);
        for (let i = rest.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [rest[i], rest[j]] = [rest[j], rest[i]];
        }
        const newPlaylist = current ? [current, ...rest] : rest;
        set({
          shuffle: true,
          originalPlaylist: original,
          playlist: newPlaylist,
          currentIndex: current ? 0 : -1,
        });
        if (current) {
          trackEvent(AnalyticsEvents.SONG_PLAYER_SHUFFLE_TOGGLE, {
            songId: current.id,
            title: current.title,
            shuffle: true,
          });
        }
        return;
      }

      // Turning shuffle off -> restore original playlist and current song/index
      const restored =
        s.originalPlaylist && s.originalPlaylist.length
          ? s.originalPlaylist.slice()
          : s.playlist.slice();
      const currentId = s.currentSong?.id;
      const newIndex = currentId
        ? restored.findIndex((p) => p.id === currentId)
        : -1;
      const finalIndex = newIndex >= 0 ? newIndex : 0;
      const finalCurrentSong = restored[finalIndex] ?? null;
      set({
        shuffle: false,
        playlist: restored,
        currentIndex: finalIndex,
        currentSong: finalCurrentSong,
        originalPlaylist: [],
      });
      if (finalCurrentSong) {
        trackEvent(AnalyticsEvents.SONG_PLAYER_SHUFFLE_TOGGLE, {
          songId: finalCurrentSong.id,
          title: finalCurrentSong.title,
          shuffle: false,
        });
      }
    },

    skip(seconds: number) {
      const s = get();
      if (s.isCasting) {
        const cc = castService.getController();
        if (cc && cc.player.canSeek) {
          const newTime = Math.max(
            0,
            Math.min(s.duration, s.currentTime + seconds),
          );
          cc.player.currentTime = newTime;
          cc.controller.seek();
        }
      } else {
        audioPlayer.skip(seconds);
      }
      const song = s.currentSong;
      if (song) {
        const event =
          seconds > 0
            ? AnalyticsEvents.SONG_PLAYER_SKIP_FORWARD
            : AnalyticsEvents.SONG_PLAYER_SKIP_BACKWARD;
        trackEvent(event, { songId: song.id, title: song.title, seconds });
      }
    },

    // Mini player dialog control (manages history state when possible)
    open(navigationType?: "push" | "replace" | "auto") {
      if (typeof window === "undefined") return;
      const s = get();
      if (!s.currentSong) {
        console.warn("Trying to open mini player with no current song");
        return;
      }
      const isAlreadyOpen = s.bigPlayerOpen;
      let navType = navigationType || "auto";
      if (navType === "auto") {
        navType = isAlreadyOpen ? "replace" : "push";
      }

      if (!isAlreadyOpen) {
        set({ bigPlayerOpen: true });
        trackEvent(AnalyticsEvents.SONG_PLAYER_OPEN, {
          songId: s.currentSong.id,
          title: s.currentSong.title,
        });
      }

      window.history[navType === "replace" ? "replaceState" : "pushState"](
        getRouteState(s.currentSong),
        "",
        s.currentSong.frontendUrl,
      );
    },

    close() {
      const router = get().router;
      if (!router) return;
      const song = get().currentSong;
      if (song) {
        trackEvent(AnalyticsEvents.SONG_PLAYER_CLOSE, {
          songId: song.id,
          title: song.title,
        });
      }
      if (
        window.history &&
        window.history.length > 1 &&
        window.history.state?.safeToGoBack
      ) {
        router.back();
      } else {
        const fallbackUrl = window.history?.state?.prevPageUrl || "/";
        router.replace(fallbackUrl);
      }

      set({ bigPlayerOpen: false });
    },

    setRouter(router: AppRouterInstance) {
      set({ router });
    },

    setBigPlayerOpen(open: boolean) {
      set({ bigPlayerOpen: open });
    },

    updateCurrentSong(updates: Partial<Song>) {
      set((s) => ({
        currentSong: s.currentSong
          ? { ...s.currentSong, ...updates }
          : s.currentSong,
        playlist: s.playlist.map((song) =>
          song.id === s.currentSong?.id ? { ...song, ...updates } : song,
        ),
      }));
    },
  };
});
